"use client";

import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "../ui/loading-spinner";
import toast from "react-hot-toast";

interface FileType {
  name: string;
  type: string;
  [key: string]: any;
}

interface DropzoneProps {
  title?: string;
  description?: string;
  onChange?: (files: FileType[]) => void;
  loading?: boolean;
}

export default function Dropzone({
  title = "File Upload",
  description = "Drag and drop files or click to select from your local machine.",
  loading = false,
  onChange,
}: DropzoneProps) {
  const [files, setFiles] = useState<FileType[]>([]);

  const allowedFileTypes = [
    "application/pdf", 
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", 
    "application/msword", 
    "image/jpeg",
    "image/png",
    "text/csv", 
    "text/plain", 
    "audio/mpeg",
    "audio/wav", 
    "audio/ogg", 
    "video/mp4", 
    "video/ogg",
    "message/rfc822",
  ];
  
  const handleDrop = (acceptedFiles: FileType[]) => {
    // Filter files based on allowed types
    const validFiles = acceptedFiles.filter((file) =>
      allowedFileTypes.includes(file.type)
    );

    if (validFiles.length < acceptedFiles.length) {
      toast.error("Some files were rejected due to unsupported formats.");
    }

    // Prevent exceeding 10 files
    if (files.length + validFiles.length > 10) {
      toast.error("You can only upload upto 10 files.");
      return;
    }
    
    if (validFiles.length > 5){
      toast.error("You can upload up to 5 files at a time.");
      return;
    }

    const newFiles = [...files, ...validFiles];
    setFiles(newFiles);

    if (onChange) {
      onChange(newFiles);
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    // if (onChange) {
    //   onChange(newFiles);
    // }
  };

  return (
    <Card className="p-6 space-y-6 border-transparent">
      <div className="space-y-2">
        <h3 className="text-2xl font-bold text-center">{title}</h3>
        <p className="text-muted-foreground text-center">{description}</p>
      </div>
      <div
        className="flex flex-col items-center justify-center gap-4 p-6 border-2 border-dashed rounded-lg bg-muted hover:bg-muted/80 transition-colors"
        onClick={() => document.getElementById("fileInput")?.click()}
        onTouchStart={() => document.getElementById("fileInput")?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const droppedFiles = Array.from(e.dataTransfer.files);
          handleDrop(droppedFiles);
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <CloudUploadIcon className="w-12 h-12 text-muted-foreground" />
        )}
        <p className="text-muted-foreground">
          {loading
            ? "Processing..."
            : "Drag and drop files here or click to select files."}
        </p>
        <input
          id="fileInput"
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) {
              handleDrop(Array.from(e.target.files));
            }
          }}
          accept={allowedFileTypes.join(",")}
        />
      </div>
      {files.length > 0 && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {files.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-muted p-3 rounded-md"
              >
                <div className="flex items-center gap-3 overflow-hidden">
                  <FileIcon className="w-6 h-6 text-muted-foreground flex-shrink-0" />
                  <p className="truncate">{file.name}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFile(index)}
                >
                  <XIcon className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function CloudUploadIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
      <path d="M12 12v9" />
      <path d="m16 16-4-4-4 4" />
    </svg>
  );
}

function FileIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    </svg>
  );
}

function XIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
